import { useRef, useState } from 'react';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { TaskList } from '@tiptap/extension-task-list';
import { TaskItem } from '@tiptap/extension-task-item';
import { TableKit } from '@tiptap/extension-table';
import {
  Bold,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  Link2,
  List,
  ListOrdered,
  Minus,
  Quote,
  Redo2,
  Smile,
  SquareCode,
  Strikethrough,
  Underline as UnderlineIcon,
  Undo2,
} from 'lucide-react';

import { ResizableImage } from '@/components/ui/block-editor/resizableImage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

// Shared prose styling for rendered marks/blocks.
const PROSE =
  '[&_h1]:text-lg [&_h1]:font-semibold [&_h2]:text-base [&_h2]:font-semibold [&_h3]:text-sm [&_h3]:font-semibold ' +
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 ' +
  '[&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground ' +
  '[&_a]:text-primary [&_a]:underline [&_hr]:my-3 [&_hr]:border-t ' +
  '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_pre]:rounded [&_pre]:bg-muted [&_pre]:p-2 [&_pre_code]:bg-transparent [&_pre_code]:px-0 ' +
  '[&_ul[data-type=taskList]]:list-none [&_ul[data-type=taskList]]:pl-1 ' +
  '[&_ul[data-type=taskList]_li]:flex [&_ul[data-type=taskList]_li]:items-start [&_ul[data-type=taskList]_li]:gap-2 ' +
  '[&_ul[data-type=taskList]_li>label]:mt-0.5 ' +
  '[&_.tableWrapper]:my-2 [&_.tableWrapper]:overflow-x-auto ' +
  '[&_table]:my-2 [&_table]:w-full [&_table]:table-fixed [&_table]:border-collapse ' +
  '[&_th]:border [&_th]:bg-muted [&_th]:p-1.5 [&_th]:text-left [&_td]:border [&_td]:p-1.5';

// Placeholder shown by @tiptap/extension-placeholder on the empty first line.
const PLACEHOLDER =
  '[&_.is-editor-empty:first-child]:before:text-muted-foreground ' +
  "[&_.is-editor-empty:first-child]:before:content-[attr(data-placeholder)] " +
  '[&_.is-editor-empty:first-child]:before:pointer-events-none ' +
  '[&_.is-editor-empty:first-child]:before:float-left [&_.is-editor-empty:first-child]:before:h-0';

const BOXED = `min-h-32 max-w-none rounded-md border bg-background px-3 py-2 text-sm focus:outline-none ${PROSE} ${PLACEHOLDER}`;
const MINIMAL = `min-h-7 max-w-none px-0 py-1 text-sm focus:outline-none ${PROSE} ${PLACEHOLDER}`;

// Nodes the rest of the app stores in descriptions (Pages' block editor,
// Confluence import, MCP writes). TipTap drops any node missing from the
// schema, and the editor patches on blur, so leaving one out would erase it
// from the record (DEV13-122). No toolbar buttons for these — preserved and
// rendered, not authored here.
const STORED_NODES = [
  TaskList,
  TaskItem.configure({ nested: true }),
  TableKit,
  ResizableImage.configure({ inline: false, allowBase64: true }),
];

// Don't navigate away when clicking a link while editing.
const STARTER_KIT = StarterKit.configure({ link: { openOnClick: false, autolink: true } });

interface RichTextEditorProps {
  /** Initial HTML. The editor owns content after mount — remount (via `key`)
   * to load a different record. */
  value: string;
  /** Called on blur with the current HTML when it changed (mirrors the drawer's
   * patch-on-blur). */
  onChange: (html: string) => void;
  placeholder?: string;
  /** Plane-style inline description: borderless, toolbar only while focused. */
  minimal?: boolean;
  /** Focus the end of the content on mount. Only for an editor the user just
   * opened (an explicit Edit), so focus follows their action. */
  focusOnMount?: boolean;
}

export function RichTextEditor({ value, onChange, placeholder, minimal, focusOnMount }: RichTextEditorProps) {
  const [focused, setFocused] = useState(false);
  // HTML as TipTap last normalised it. A blur with nothing edited must not
  // report a "change" — TipTap's serialiser rewrites stored HTML (colgroups,
  // attribute order, a trailing empty paragraph after a table or list), which
  // would otherwise patch the record on every focus. Taken on first focus, once
  // those post-create normalisations have run.
  const lastHtml = useRef<string | null>(null);
  const editor = useEditor({
    extensions: [STARTER_KIT, ...STORED_NODES, Placeholder.configure({ placeholder: placeholder ?? '' })],
    content: value || '',
    autofocus: focusOnMount ? 'end' : false,
    editorProps: { attributes: { class: minimal ? MINIMAL : BOXED } },
    onFocus: ({ editor: e }) => {
      setFocused(true);
      lastHtml.current ??= e.isEmpty ? '' : e.getHTML();
    },
    onBlur: ({ editor: e }) => {
      setFocused(false);
      const html = e.isEmpty ? '' : e.getHTML();
      if (html === lastHtml.current) return;
      lastHtml.current = html;
      onChange(html);
    },
  });

  if (!editor) return null;

  // In minimal mode the toolbar only appears while editing (Plane behaviour).
  const showToolbar = !minimal || focused;

  return (
    <div className="space-y-1.5">
      {showToolbar ? <Toolbar editor={editor} /> : null}
      <EditorContent editor={editor} />
    </div>
  );
}

/** Read-only render of stored rich HTML (non-editable TipTap instance — no raw
 * `dangerouslySetInnerHTML`; the schema is TipTap-controlled). */
export function RichTextContent({ html }: { html: string }) {
  const editor = useEditor(
    {
      extensions: [StarterKit, ...STORED_NODES],
      content: html || '',
      editable: false,
      editorProps: { attributes: { class: cn(MINIMAL, 'min-h-0') } },
    },
    // Re-render when the stored HTML changes (e.g. after a save).
    [html],
  );
  if (!editor) return null;
  return <EditorContent editor={editor} />;
}

const BTN = 'rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:hover:bg-transparent';
const ACTIVE = 'bg-muted text-foreground';

interface Tool {
  icon: typeof Bold;
  label: string;
  isActive?: () => boolean;
  disabled?: () => boolean;
  run: () => void;
}

function ToolButton({ tool }: { tool: Tool }) {
  const Icon = tool.icon;
  const active = tool.isActive?.() ?? false;
  return (
    <button
      type="button"
      aria-label={tool.label}
      title={tool.label}
      aria-pressed={active}
      disabled={tool.disabled?.() ?? false}
      // Keep editor focus/selection: a plain click would blur the editor first
      // (collapsing the selection + firing onBlur), so the command would apply
      // to nothing — and in minimal mode the toolbar would unmount mid-click.
      onMouseDown={(e) => e.preventDefault()}
      onClick={tool.run}
      className={cn(BTN, active && ACTIVE)}
    >
      <Icon className="size-4" />
    </button>
  );
}

function Divider() {
  return <span className="bg-border mx-0.5 h-5 w-px" aria-hidden />;
}

function Toolbar({ editor }: { editor: Editor }) {
  const headings: Tool[] = [
    { icon: Heading1, label: 'Heading 1', isActive: () => editor.isActive('heading', { level: 1 }), run: () => editor.chain().focus().toggleHeading({ level: 1 }).run() },
    { icon: Heading2, label: 'Heading 2', isActive: () => editor.isActive('heading', { level: 2 }), run: () => editor.chain().focus().toggleHeading({ level: 2 }).run() },
    { icon: Heading3, label: 'Heading 3', isActive: () => editor.isActive('heading', { level: 3 }), run: () => editor.chain().focus().toggleHeading({ level: 3 }).run() },
  ];
  const marks: Tool[] = [
    { icon: Bold, label: 'Bold', isActive: () => editor.isActive('bold'), run: () => editor.chain().focus().toggleBold().run() },
    { icon: Italic, label: 'Italic', isActive: () => editor.isActive('italic'), run: () => editor.chain().focus().toggleItalic().run() },
    { icon: UnderlineIcon, label: 'Underline', isActive: () => editor.isActive('underline'), run: () => editor.chain().focus().toggleUnderline().run() },
    { icon: Strikethrough, label: 'Strikethrough', isActive: () => editor.isActive('strike'), run: () => editor.chain().focus().toggleStrike().run() },
    { icon: Code, label: 'Inline code', isActive: () => editor.isActive('code'), run: () => editor.chain().focus().toggleCode().run() },
  ];
  const blocks: Tool[] = [
    { icon: List, label: 'Bullet list', isActive: () => editor.isActive('bulletList'), run: () => editor.chain().focus().toggleBulletList().run() },
    { icon: ListOrdered, label: 'Numbered list', isActive: () => editor.isActive('orderedList'), run: () => editor.chain().focus().toggleOrderedList().run() },
    { icon: Quote, label: 'Quote', isActive: () => editor.isActive('blockquote'), run: () => editor.chain().focus().toggleBlockquote().run() },
    { icon: SquareCode, label: 'Code block', isActive: () => editor.isActive('codeBlock'), run: () => editor.chain().focus().toggleCodeBlock().run() },
    { icon: Minus, label: 'Divider', run: () => editor.chain().focus().setHorizontalRule().run() },
  ];
  const history: Tool[] = [
    { icon: Undo2, label: 'Undo', disabled: () => !editor.can().undo(), run: () => editor.chain().focus().undo().run() },
    { icon: Redo2, label: 'Redo', disabled: () => !editor.can().redo(), run: () => editor.chain().focus().redo().run() },
  ];

  return (
    <div className="flex flex-wrap items-center gap-0.5 rounded-md border p-1">
      {headings.map((t) => <ToolButton key={t.label} tool={t} />)}
      <Divider />
      {marks.map((t) => <ToolButton key={t.label} tool={t} />)}
      <LinkButton editor={editor} />
      <EmojiButton editor={editor} />
      <Divider />
      {blocks.map((t) => <ToolButton key={t.label} tool={t} />)}
      <Divider />
      {history.map((t) => <ToolButton key={t.label} tool={t} />)}
    </div>
  );
}

function LinkButton({ editor }: { editor: Editor }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const active = editor.isActive('link');

  function apply() {
    const href = url.trim();
    const chain = editor.chain().focus().extendMarkRange('link');
    if (href) chain.setLink({ href }).run();
    else chain.unsetLink().run();
    setOpen(false);
  }

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        if (o) setUrl((editor.getAttributes('link').href as string) ?? '');
        setOpen(o);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Link"
          title="Link"
          aria-pressed={active}
          onMouseDown={(e) => e.preventDefault()}
          className={cn(BTN, active && ACTIVE)}
        >
          <Link2 className="size-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 space-y-2">
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && apply()}
          placeholder="https://example.com"
          className="h-8"
        />
        <div className="flex justify-end gap-2">
          {active ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                editor.chain().focus().extendMarkRange('link').unsetLink().run();
                setOpen(false);
              }}
            >
              Remove
            </Button>
          ) : null}
          <Button size="sm" onClick={apply} disabled={!url.trim() && !active}>
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const EMOJIS =
  '😀 😄 😁 😅 😂 🙂 😉 😍 🤩 🤔 😎 😴 🙌 👍 👎 👏 🙏 💪 🔥 ✨ ⭐ 🎉 ✅ ❌ ⚠️ ❤️ 💙 💚 💜 🚀 🐛 🛠️ 📌 📎 📊 📈 💡 🧠 ⏰ 📅'.split(
    ' ',
  );

function EmojiButton({ editor }: { editor: Editor }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Emoji"
          title="Emoji"
          onMouseDown={(e) => e.preventDefault()}
          className={BTN}
        >
          <Smile className="size-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2">
        <div className="grid grid-cols-8 gap-0.5">
          {EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              onClick={() => {
                editor.chain().focus().insertContent(emoji).run();
                setOpen(false);
              }}
              className="hover:bg-muted rounded p-1 text-lg leading-none"
            >
              {emoji}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
